/* eslint-disable */
import React, { useState, useEffect } from 'react';
import Framework from '@greenville/framework';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import { Typography } from '@material-ui/core';
import { Alert, Box, Chip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import getAgenticTools from '../service/getAgenticTools';
import {
  ADDITIONAL_METADATA_LABEL,
  ADDITIONAL_METADATA_PLACEHOLDER,
  LABEL_PARSERS
} from '../constants/AgenticTextConstants';
import { validateJavaScript } from '../utils';
import { IMPLEMENTATION_BUTTONS } from '../constants/StyleConstants';

const availableParsers = ['json-parser'];

// Modal styles
const modalStyles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 10001,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  modal: {
    backgroundColor: '#272727',
    color: '#e2e8f0',
    borderRadius: '8px',
    padding: '25px',
    width: '600px',
    maxWidth: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
    position: 'relative'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    borderBottom: '1px solid #444',
    paddingBottom: '8px'
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    color: '#e2e8f0',
    cursor: 'pointer'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
  },
  label: {
    fontWeight: 'bold',
    marginBottom: '4px'
  },
  checkbox: {
    color: '#e2e8f0'
  },
  input: {
    backgroundColor: '#333',
    color: '#e2e8f0',
    border: '1px solid #444',
    borderRadius: '4px',
    padding: '8px',
    width: '100%'
  },
  textarea: {
    backgroundColor: '#333',
    color: '#e2e8f0',
    border: '1px solid #444',
    borderRadius: '4px',
    padding: '8px',
    minHeight: '100px',
    width: '100%',
    fontFamily: 'monospace',
    resize: 'vertical'
  },
  select: {
    backgroundColor: '#333',
    color: '#e2e8f0',
    border: '1px solid #444',
    borderRadius: '4px',
    padding: '8px',
    width: '100%'
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #444',
    marginBottom: '16px'
  },
  tab: {
    padding: '8px 16px',
    cursor: 'pointer',
    borderBottom: '2px solid transparent'
  },
  activeTab: {
    borderBottom: '2px solid #a78bfa',
    fontWeight: 'bold'
  },
  tabContent: {
    padding: '8px 0'
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    marginTop: '20px'
  },
  button: {
    padding: '8px 16px',
    borderRadius: '4px',
    cursor: 'pointer',
    border: 'none',
    fontWeight: 'bold'
  },
  cancelButton: {
    backgroundColor: '#4b5563',
    color: '#e2e8f0'
  },
  saveButton: {
    backgroundColor: '#a78bfa',
    color: '#111'
  },
  validationMessage: {
    fontSize: '12px',
    marginTop: '4px',
    padding: '4px 8px',
    borderRadius: '4px',
    border: '1px solid'
  },
  errorMessage: {
    color: '#ef4444',
    backgroundColor: '#fef2f2',
    borderColor: '#fecaca'
  },
  warningMessage: {
    color: '#f59e0b',
    backgroundColor: '#fffbeb',
    borderColor: '#fed7aa'
  },
  validMessage: {
    color: '#10b981',
    borderColor: '#bbf7d0'
  }
};

const EditNodeModal = ({
  isOpen,
  onClose,
  nodeType,
  nodeData,
  onSave,
  tenantId,
  providerList,
  promptTypesList,
  canEditGraph
}) => {
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({});
  const [parameters, setParameters] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [tools, setTools] = useState([]);
  const [selected, setSelected] = useState('');
  const [jsValidation, setJsValidation] = useState({ isValid: true, errors: [], warnings: [] });
  const [isCustomTool, setIsCustomTool] = useState(false);
  const [headers, setHeaders] = useState([{ key: '', value: '' }]);
  const [payloadFields, setPayloadFields] = useState([{ key: '', value: '' }]);
  const [payloadJson, setPayloadJson] = useState('{}');
  const [payloadMode, setPayloadMode] = useState('json'); // 'fields' or 'json' - defaulting to JSON only
  const [payloadJsonValidation, setPayloadJsonValidation] = useState({
    isValid: true,
    error: '',
    showValidation: false
  });
  const [mcpConfig, setMcpConfig] = useState('{}');
  const [mcpConfigValidation, setMcpConfigValidation] = useState({
    isValid: true,
    error: '',
    showValidation: false
  });
  const selectedToolData = tools.find((tool) => tool.toolId === selected);
  const isMCPServer = selectedToolData?.isMcp === true;

  useEffect(() => {
    const getTools = async () => {
      const response = await getAgenticTools();
      setTools(response?.tools || []);
    };
    getTools();
  }, [nodeData, nodeType]);

  useEffect(() => {
    if (!isOpen || nodeType !== 'tool' || !nodeData) return;
    if (!tools || tools.length === 0) return;

    const existingToolId = nodeData?.toolId;
    const existingToolName = nodeData?.tool_name || nodeData?.config?.name;
    const matchedTool =
      tools.find((t) => t.toolId === existingToolId) || tools.find((t) => t.label === existingToolName);

    setIsCustomTool(!!matchedTool?.custom);
    if (matchedTool?.toolId) {
      setSelected((prev) => (prev ? prev : matchedTool.toolId));
    }
  }, [isOpen, nodeType, nodeData, tools]);

  // Initialize form data when the modal opens or nodeData changes
  useEffect(() => {
    // Set provider options
    const filteredProviders = providerList.filter((entry) => entry.active).map((entry) => entry.providerName);
    const uniqueProviders = Array.from(new Set(filteredProviders));
    setProviderOptions(uniqueProviders);

    if (!isOpen) {
      setFormData({});
      setParameters([]);
      setSelected('');
      setIsCustomTool(false);
      setHeaders([{ key: '', value: '' }]);
      setPayloadFields([{ key: '', value: '' }]);
      setPayloadJson('{}');
      setPayloadMode('json');
      setPayloadJsonValidation({ isValid: true, error: '', showValidation: false });
      setMcpConfig('{}');
      setMcpConfigValidation({ isValid: true, error: '', showValidation: false });
    } else if (nodeData) {
      const initialFormData = {
        // Common fields
        id: nodeData.id || '',
        customType: nodeData.customType || nodeType,

        // Agent and Supervisor specific fields
        ...((nodeType === 'agent' || nodeType === 'supervisor') && {
          prompt: nodeData.prompt || (nodeType === 'supervisor' ? 'Supervisor' : 'Research Assistant'),
          intentType: nodeData?.intentType || '',
          agentId: nodeData.agentId || (nodeType === 'supervisor' ? `supervisor_${Date.now()}` : `agent_${Date.now()}`),
          modelProvider: nodeData.config?.model?.provider || 'openai',
          modelName: nodeData.config?.model?.model || 'gpt-4o',
          temperature: nodeData.config?.model?.temperature ?? 0.7,
          maxTokens: nodeData.config?.model?.maxTokens || 1000,
          systemPrompt:
            nodeData.config?.systemPrompt ||
            (nodeType === 'supervisor'
              ? "You are a routing and output agent. Given the user's last message, carefully analyse and execute only those agents and give the output as is from the agents and in a clear and separated manner and do not mix the outputs from different agents. Also do not generate anything from your end STRICTLY. In the end just print the name of each agent that were used in individual brackets."
              : 'You are a helpful research assistant that can find information on any topic.'),
          memoryType: nodeData.config?.memory?.type || 'buffer',
          promptType: nodeData.config?.model?.promptType || '',
          maxMessages: nodeData.config?.memory?.maxMessages || 10,
          toolChoice: nodeData.config?.toolCalling?.toolChoice || 'auto',
          // GPT-5 specific fields
          reasoningEffort: nodeData.config?.agentConfig?.reasoningEffort || 'medium',
          verbosity: nodeData.config?.agentConfig?.verbosity || 'medium',
          otherParameters: JSON.stringify(
            nodeData.config?.otherParameters || {
              top_p: 0.9,
              frequency_penalty: 0.5,
              presence_penalty: 0.5
            },
            null,
            2
          ),
          additionalMetadata: JSON.stringify(nodeData.config?.additionalMetadata, null, 2),
          parsers: Array.isArray(nodeData.config?.parsers)
            ? nodeData.config.parsers
            : nodeData.config?.parsers
            ? [nodeData.config.parsers]
            : []
        }),

        // Tool specific fields
        ...(nodeType === 'tool' && {
          tool_name: nodeData.tool_name || 'add',
          toolId: nodeData.toolId || `tool_${Date.now()}`,
          description: nodeData.description || nodeData.config?.description || 'Adds two numbers together',
          responseFormat: nodeData.config?.responseFormat || '',
          // Check for both MCPServer and RestAPI configurations
          endpoint: (() => {
            // Try to get from mcpConfig first
            if (nodeData.toolConfig?.mcpConfig) {
              try {
                const mcpConfigObj =
                  typeof nodeData.toolConfig.mcpConfig === 'string'
                    ? JSON.parse(nodeData.toolConfig.mcpConfig)
                    : nodeData.toolConfig.mcpConfig;
                if (mcpConfigObj?.serverName) return mcpConfigObj.serverName;
              } catch (e) {
                // If parsing fails, continue to other sources
              }
            }
            return nodeData.toolConfig?.apiConfig?.endpoint || nodeData.config?.implementation?.endpoint || '';
          })(),
          method: (() => {
            // Try to get from mcpConfig first
            if (nodeData.toolConfig?.mcpConfig) {
              try {
                const mcpConfigObj =
                  typeof nodeData.toolConfig.mcpConfig === 'string'
                    ? JSON.parse(nodeData.toolConfig.mcpConfig)
                    : nodeData.toolConfig.mcpConfig;
                if (mcpConfigObj?.method) return mcpConfigObj.method;
              } catch (e) {
                // If parsing fails, continue to other sources
              }
            }
            return nodeData.toolConfig?.apiConfig?.method || nodeData.config?.implementation?.method || 'POST';
          })(),
          authType: (() => {
            // Try to get from mcpConfig first
            if (nodeData.toolConfig?.mcpConfig) {
              try {
                const mcpConfigObj =
                  typeof nodeData.toolConfig.mcpConfig === 'string'
                    ? JSON.parse(nodeData.toolConfig.mcpConfig)
                    : nodeData.toolConfig.mcpConfig;
                if (mcpConfigObj?.authType) return mcpConfigObj.authType;
              } catch (e) {
                // If parsing fails, continue to other sources
              }
            }
            return nodeData.toolConfig?.apiConfig?.authType || nodeData.config?.implementation?.authType || 'None';
          })(),
          implementation:
            nodeData.config?.implementation?.code ||
            `async ({ a, b }) => {
  console.log('a', a);
  console.log('b', b);
  return a + b;
}`,
          advancedOptions: JSON.stringify(
            nodeData.toolConfig?.advancedOptions || nodeData.config?.advancedOptions || {},
            null,
            2
          )
        })
      };

      setFormData(initialFormData);

      // Initialize mcpConfig for MCPServer tools
      if (nodeType === 'tool' && nodeData.toolConfig?.mcpConfig) {
        // If mcpConfig is already a string, use it as-is; otherwise stringify it
        const configValue =
          typeof nodeData.toolConfig.mcpConfig === 'string'
            ? nodeData.toolConfig.mcpConfig
            : JSON.stringify(nodeData.toolConfig.mcpConfig, null, 2);
        setMcpConfig(configValue);
      } else {
        setMcpConfig('{}');
      }

      // Initialize headers for tools - check both apiConfig and mcpConfig
      if (nodeType === 'tool') {
        let existingHeaders = null;

        // Check for MCPServer configuration first
        if (nodeData.toolConfig?.mcpConfig) {
          try {
            const mcpConfigObj =
              typeof nodeData.toolConfig.mcpConfig === 'string'
                ? JSON.parse(nodeData.toolConfig.mcpConfig)
                : nodeData.toolConfig.mcpConfig;
            if (mcpConfigObj?.headers) {
              existingHeaders = mcpConfigObj.headers;
            }
          } catch (e) {
            // If parsing fails, skip mcpConfig headers
          }
        }
        // Fallback to apiConfig for RestAPI
        else if (nodeData.toolConfig?.apiConfig?.headers) {
          existingHeaders = nodeData.toolConfig.apiConfig.headers;
        }

        if (existingHeaders) {
          const headerArray = Object.entries(existingHeaders).map(([key, value]) => ({ key, value }));
          setHeaders(headerArray.length > 0 ? headerArray : [{ key: '', value: '' }]);
        } else {
          setHeaders([{ key: '', value: '' }]);
        }
      } else {
        setHeaders([{ key: '', value: '' }]);
      }

      // Initialize payload for tools - check both apiConfig and mcpConfig
      if (nodeType === 'tool') {
        let existingPayload = null;

        // Check for MCPServer configuration first
        if (nodeData.toolConfig?.mcpConfig) {
          try {
            const mcpConfigObj =
              typeof nodeData.toolConfig.mcpConfig === 'string'
                ? JSON.parse(nodeData.toolConfig.mcpConfig)
                : nodeData.toolConfig.mcpConfig;
            if (mcpConfigObj?.payload) {
              existingPayload = mcpConfigObj.payload;
            }
          } catch (e) {
            // If parsing fails, skip mcpConfig payload
          }
        }
        // Fallback to apiConfig for RestAPI
        else if (nodeData.toolConfig?.apiConfig?.payload) {
          existingPayload = nodeData.toolConfig.apiConfig.payload;
        }

        if (existingPayload) {
          if (typeof existingPayload === 'object' && existingPayload !== null) {
            // Always use JSON mode for objects
            setPayloadJson(JSON.stringify(existingPayload, null, 2));
            setPayloadFields([{ key: '', value: '' }]);
            setPayloadMode('json');
          } else if (typeof existingPayload === 'string') {
            // If payload is a string, use as-is for JSON mode
            setPayloadJson(existingPayload);
            setPayloadFields([{ key: '', value: '' }]);
            setPayloadMode('json');
          }
        } else {
          setPayloadFields([{ key: '', value: '' }]);
          setPayloadJson('{}');
          setPayloadMode('json');
        }
      } else {
        setPayloadFields([{ key: '', value: '' }]);
        setPayloadJson('{}');
        setPayloadMode('json');
      }

      // Parse existing parameters for tools
      if (nodeType === 'tool' && nodeData.config?.parameters) {
        try {
          const schema = nodeData.config.parameters;
          const params = Object.entries(schema.properties || {}).map(([name, def]) => ({
            name,
            type: def.type,
            description: def.description || '',
            required: (schema.required || []).includes(name)
          }));
          setParameters(params);
        } catch (error) {
          console.error('Error parsing parameters:', error);
          setParameters([]);
        }
      }
    }
  }, [nodeData, nodeType, isOpen, providerList, tenantId]);

  // Reset selected tool when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelected('');
    }
  }, [isOpen]);

  useEffect(() => {
    const providerData = providerList;

    if (providerData && Array.isArray(providerData)) {
      const filteredProviders = providerData.filter((entry) => entry.active).map((entry) => entry.providerName);

      const uniqueProviders = Array.from(new Set(filteredProviders));

      if (nodeData?.config?.model?.provider && nodeData?.config?.model?.model) {
        const filteredModels = providerList
          .filter((entry) => entry.providerName === nodeData?.config?.model?.provider && entry.active)
          .map((entry) => entry.model);
        const uniqueModels = Array.from(new Set(filteredModels));
        setFormData((prev) => ({
          ...prev,
          modelProvider: uniqueProviders.includes(nodeData?.config?.model?.provider)
            ? nodeData?.config?.model?.provider
            : '',
          modelName: uniqueModels.includes(nodeData?.config?.model?.model) ? nodeData?.config?.model?.model : ''
        }));
      }

      setProviderOptions(uniqueProviders);
    }
  }, [isOpen, nodeData, providerList, tenantId]);

  // Step 2: When provider changes, update models
  useEffect(() => {
    const selectedProvider = formData.modelProvider;
    if (selectedProvider) {
      const filteredModels = providerList
        .filter((entry) => entry.providerName === selectedProvider && entry.active)
        .map((entry) => entry.model);

      const uniqueModels = Array.from(new Set(filteredModels));
      setModelOptions(uniqueModels);

      setFormData((prev) => ({
        ...prev,
        modelName: uniqueModels.includes(nodeData?.config?.model?.model) ? nodeData?.config?.model?.model : ''
      }));
    } else {
      setModelOptions([]);
      setFormData((prev) => ({
        ...prev,
        modelName: ''
      }));
    }
  }, [formData.modelProvider, nodeData, providerList, tenantId]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      parsers: checked ? [...prev.parsers, name] : prev.parsers.filter((item) => item !== name)
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: parseFloat(value)
    }));
  };

  const handleToolChange = (e) => {
    const selectedValue = e.target.value;
    setSelected(selectedValue);
    const selectedToolData = tools.find((tool) => tool.toolId === selectedValue);

    const selectedToolFormData = {
      ...formData,
      tool_name: selectedToolData?.label || '',
      toolId: selectedToolData?.toolId,
      description: selectedToolData?.description || '',
      implementation: selectedToolData?.toolConfig?.jsMethodCode || '',
      responseFormat: selectedToolData?.toolConfig?.responseFormat || '',
      // Check for both MCPServer and RestAPI configurations
      endpoint: (() => {
        // Try to get from mcpConfig first
        if (selectedToolData?.toolConfig?.mcpConfig) {
          try {
            const mcpConfigObj =
              typeof selectedToolData.toolConfig.mcpConfig === 'string'
                ? JSON.parse(selectedToolData.toolConfig.mcpConfig)
                : selectedToolData.toolConfig.mcpConfig;
            if (mcpConfigObj?.serverName) return mcpConfigObj.serverName;
          } catch (e) {
            // If parsing fails, continue to other sources
          }
        }
        return selectedToolData?.toolConfig?.apiConfig?.endpoint || '';
      })(),
      method: (() => {
        // Try to get from mcpConfig first
        if (selectedToolData?.toolConfig?.mcpConfig) {
          try {
            const mcpConfigObj =
              typeof selectedToolData.toolConfig.mcpConfig === 'string'
                ? JSON.parse(selectedToolData.toolConfig.mcpConfig)
                : selectedToolData.toolConfig.mcpConfig;
            if (mcpConfigObj?.method) return mcpConfigObj.method;
          } catch (e) {
            // If parsing fails, continue to other sources
          }
        }
        return selectedToolData?.toolConfig?.apiConfig?.method || 'POST';
      })(),
      authType: (() => {
        // Try to get from mcpConfig first
        if (selectedToolData?.toolConfig?.mcpConfig) {
          try {
            const mcpConfigObj =
              typeof selectedToolData.toolConfig.mcpConfig === 'string'
                ? JSON.parse(selectedToolData.toolConfig.mcpConfig)
                : selectedToolData.toolConfig.mcpConfig;
            if (mcpConfigObj?.authType) return mcpConfigObj.authType;
          } catch (e) {
            // If parsing fails, continue to other sources
          }
        }
        return selectedToolData?.toolConfig?.apiConfig?.authType || 'None';
      })(),
      advancedOptions: JSON.stringify(selectedToolData?.toolConfig?.advancedOptions || {}, null, 2)
    };

    setIsCustomTool(!!selectedToolData?.custom);
    setFormData(selectedToolFormData);

    // Initialize mcpConfig for selected MCPServer tool
    if (selectedToolData?.toolConfig?.mcpConfig) {
      // If mcpConfig is already a string, use it as-is; otherwise stringify it
      const configValue =
        typeof selectedToolData.toolConfig.mcpConfig === 'string'
          ? selectedToolData.toolConfig.mcpConfig
          : JSON.stringify(selectedToolData.toolConfig.mcpConfig, null, 2);
      setMcpConfig(configValue);
    } else {
      setMcpConfig('{}');
    }

    // Initialize headers for selected tool - check both apiConfig and mcpConfig
    let existingHeaders = null;
    if (selectedToolData?.toolConfig?.mcpConfig) {
      try {
        const mcpConfigObj =
          typeof selectedToolData.toolConfig.mcpConfig === 'string'
            ? JSON.parse(selectedToolData.toolConfig.mcpConfig)
            : selectedToolData.toolConfig.mcpConfig;
        if (mcpConfigObj?.headers) {
          existingHeaders = mcpConfigObj.headers;
        }
      } catch (e) {
        // If parsing fails, skip mcpConfig headers
      }
    } else if (selectedToolData?.toolConfig?.apiConfig?.headers) {
      existingHeaders = selectedToolData.toolConfig.apiConfig.headers;
    }

    if (existingHeaders) {
      const headerArray = Object.entries(existingHeaders).map(([key, value]) => ({ key, value }));
      setHeaders(headerArray.length > 0 ? headerArray : [{ key: '', value: '' }]);
    } else {
      setHeaders([{ key: '', value: '' }]);
    }

    // Initialize payload for selected tool - check both apiConfig and mcpConfig
    let existingPayload = null;
    if (selectedToolData?.toolConfig?.mcpConfig) {
      try {
        const mcpConfigObj =
          typeof selectedToolData.toolConfig.mcpConfig === 'string'
            ? JSON.parse(selectedToolData.toolConfig.mcpConfig)
            : selectedToolData.toolConfig.mcpConfig;
        if (mcpConfigObj?.payload) {
          existingPayload = mcpConfigObj.payload;
        }
      } catch (e) {
        // If parsing fails, skip mcpConfig payload
      }
    } else if (selectedToolData?.toolConfig?.apiConfig?.payload) {
      existingPayload = selectedToolData.toolConfig.apiConfig.payload;
    }

    if (existingPayload) {
      if (typeof existingPayload === 'object' && existingPayload !== null) {
        // Always use JSON mode for objects
        setPayloadJson(JSON.stringify(existingPayload, null, 2));
        setPayloadFields([{ key: '', value: '' }]);
        setPayloadMode('json');
      } else if (typeof existingPayload === 'string') {
        // If payload is a string, use as-is for JSON mode
        setPayloadJson(existingPayload);
        setPayloadFields([{ key: '', value: '' }]);
        setPayloadMode('json');
      }
    } else {
      setPayloadFields([{ key: '', value: '' }]);
      setPayloadJson('{}');
      setPayloadMode('json');
    }

    let newParameters = [];
    if (selectedToolData?.toolConfig?.zodSchema) {
      try {
        const schema = JSON.parse(selectedToolData.toolConfig.zodSchema);
        newParameters = Object.entries(schema.properties || {}).map(([name, def]) => ({
          name,
          type: def.type || 'string',
          description: def.description || '',
          required: (schema.required || []).includes(name)
        }));
      } catch (error) {
        console.error('Error parsing selected tool parameters:', error);
      }
    }
    setParameters(newParameters);
  };

  const handleParameterChange = (index, field, value) => {
    const newParameters = [...parameters];
    newParameters[index] = { ...newParameters[index], [field]: value };
    setParameters(newParameters);
  };

  const addParameter = () => {
    setParameters([
      ...parameters,
      {
        name: '',
        type: 'number',
        description: '',
        required: true
      }
    ]);
  };

  const removeParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const handleHeaderChange = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index] = { ...newHeaders[index], [field]: value };
    setHeaders(newHeaders);
  };

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const removeHeader = (index) => {
    if (headers.length > 1) {
      setHeaders(headers.filter((_, i) => i !== index));
    } else {
      setHeaders([{ key: '', value: '' }]);
    }
  };

  // COMMENTED OUT - Field view no longer supported, JSON only
  // const handlePayloadFieldChange = (index, field, value) => {
  //   const newPayloadFields = [...payloadFields];
  //   newPayloadFields[index] = { ...newPayloadFields[index], [field]: value };
  //   setPayloadFields(newPayloadFields);

  //   // Update JSON representation
  //   const payloadObj = newPayloadFields.reduce((acc, field) => {
  //     if (field.key && field.value) {
  //       acc[field.key] = field.value;
  //     }
  //     return acc;
  //   }, {});
  //   setPayloadJson(JSON.stringify(payloadObj, null, 2));
  // };

  // COMMENTED OUT - Field view no longer supported, JSON only
  // const addPayloadField = () => {
  //   setPayloadFields([...payloadFields, { key: '', value: '' }]);
  // };

  // COMMENTED OUT - Field view no longer supported, JSON only
  // const removePayloadField = (index) => {
  //   if (payloadFields.length > 1) {
  //     const newFields = payloadFields.filter((_, i) => i !== index);
  //     setPayloadFields(newFields);

  //     // Update JSON representation
  //     const payloadObj = newFields.reduce((acc, field) => {
  //       if (field.key && field.value) {
  //         acc[field.key] = field.value;
  //       }
  //       return acc;
  //     }, {});
  //     setPayloadJson(JSON.stringify(payloadObj, null, 2));
  //   } else {
  //     setPayloadFields([{ key: '', value: '' }]);
  //     setPayloadJson('{}');
  //   }
  // };

  const handlePayloadJsonChange = (e) => {
    const value = e.target.value;
    setPayloadJson(value);

    // Reset validation display when user types
    setPayloadJsonValidation((prev) => ({ ...prev, showValidation: false }));
  };

  const validatePayloadJson = () => {
    try {
      const parsed = JSON.parse(payloadJson);
      setPayloadJsonValidation({ isValid: true, error: '', showValidation: true });

      // Update fields representation if valid
      if (typeof parsed === 'object' && parsed !== null) {
        const fieldsArray = Object.entries(parsed).map(([key, val]) => ({ key, value: String(val) }));
        setPayloadFields(fieldsArray.length > 0 ? fieldsArray : [{ key: '', value: '' }]);
      }
    } catch (error) {
      setPayloadJsonValidation({ isValid: false, error: error.message, showValidation: true });
    }
  };

  const handleMcpConfigChange = (e) => {
    setMcpConfig(e.target.value);
    setMcpConfigValidation({ ...mcpConfigValidation, showValidation: false });
  };

  const validateMcpConfig = () => {
    try {
      JSON.parse(mcpConfig);
      setMcpConfigValidation({
        isValid: true,
        error: '',
        showValidation: true
      });
    } catch (error) {
      setMcpConfigValidation({
        isValid: false,
        error: error.message,
        showValidation: true
      });
    }
  };

  // COMMENTED OUT - Field view no longer supported, JSON only
  // const togglePayloadMode = () => {
  //   if (payloadMode === 'fields') {
  //     // Switching to JSON mode - ensure JSON is up to date
  //     const payloadObj = payloadFields.reduce((acc, field) => {
  //       if (field.key && field.value) {
  //         acc[field.key] = field.value;
  //       }
  //       return acc;
  //     }, {});
  //     setPayloadJson(JSON.stringify(payloadObj, null, 2));
  //     setPayloadMode('json');
  //   } else {
  //     // Switching to fields mode - parse JSON if valid
  //     try {
  //       const parsed = JSON.parse(payloadJson);
  //       if (typeof parsed === 'object' && parsed !== null) {
  //         const fieldsArray = Object.entries(parsed).map(([key, val]) => ({ key, value: val }));
  //         setPayloadFields(fieldsArray.length > 0 ? fieldsArray : [{ key: '', value: '' }]);
  //       }
  //       setPayloadMode('json');
  //     } catch (error) {
  //       // If JSON is invalid, don't switch modes
  //       setPayloadJsonValidation({ isValid: false, error: error.message, showValidation: true });
  //     }
  //   }
  // };

  const generateDefaultImplementation = () => {
    const paramList = parameters.map((p) => p.name).join(', ');
    return `async ({ ${paramList} }) => {
  // Implementation goes here
  return result;
}`;
  };

  const handleImplementationChange = (e) => {
    const value = e?.target?.value;
    setFormData((prev) => ({
      ...prev,
      implementation: value
    }));
  };

  const regenerateImplementation = () => {
    const newImplementation = generateDefaultImplementation();
    setFormData((prev) => ({
      ...prev,
      implementation: newImplementation
    }));
  };

  const validateImplementation = (implementation) => {
    const validation = validateJavaScript(implementation);
    setJsValidation(validation);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (nodeType === 'tool') {
      // Generate Zod schema from parameters
      const zodSchema = {
        type: 'object',
        properties: {},
        required: []
      };

      parameters.forEach((param) => {
        zodSchema.properties[param.name] = {
          type: param.type,
          description: param.description
        };
        if (param.required) {
          zodSchema.required.push(param.name);
        }
      });

      // Get the selected tool to determine its type using isMcp property
      const selectedToolData = tools.find((tool) => tool.toolId === selected);
      const isMCPServer = selectedToolData?.isMcp === true;

      const updatedData = {
        ...nodeData,
        tool_name: formData.tool_name,
        toolId: formData.toolId,
        customType: formData.customType,
        description: formData.description,
        config: {
          ...nodeData.config,
          name: formData.tool_name,
          description: formData.description,
          parameters: zodSchema,
          responseFormat: formData.responseFormat,
          implementation: {
            type: 'function',
            code: formData.implementation,
            endpoint: formData.endpoint,
            method: formData.method,
            authType: formData.authType
          }
        },
        toolConfig: {
          ...nodeData.toolConfig,
          zodSchema: JSON.stringify(zodSchema),
          jsMethod: formData.tool_name,
          jsMethodCode: formData.implementation,
          // Conditional configuration based on tool type
          ...(isMCPServer
            ? {
                // MCPServer configuration - store as JSON object
                mcpConfig: (() => {
                  try {
                    return JSON.parse(mcpConfig);
                  } catch (e) {
                    return {};
                  }
                })(),
                advancedOptions: (() => {
                  try {
                    return JSON.parse(formData.advancedOptions || '{}');
                  } catch (e) {
                    return {};
                  }
                })()
              }
            : {
                // RestAPI configuration (default)
                apiConfig: {
                  endpoint: formData.endpoint,
                  method: formData.method,
                  authType: formData.authType,
                  headers: headers.reduce((acc, header) => {
                    if (header.key && header.value) {
                      acc[header.key] = header.value;
                    }
                    return acc;
                  }, {}),
                  ...(formData.method === 'POST' && {
                    payload: (() => {
                      try {
                        return JSON.parse(payloadJson);
                      } catch (e) {
                        return payloadJson; // fallback to string if JSON is invalid
                      }
                    })()
                  })
                },
                advancedOptions: (() => {
                  try {
                    return JSON.parse(formData.advancedOptions || '{}');
                  } catch (e) {
                    return {};
                  }
                })()
              })
        }
      };
      onSave(updatedData);
    } else if (nodeType === 'agent' || nodeType === 'supervisor') {
      // Parse otherParameters from JSON string
      let otherParameters = {};
      try {
        otherParameters = JSON.parse(formData.otherParameters || '{}');
      } catch (error) {
        const notification = {
          open: true,
          type: 'error',
          message: 'Other Parameters JSON is invalid. Please check the format.'
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
        return;
      }

      let additionalMetadata = {};
      try {
        additionalMetadata = JSON.parse(formData.additionalMetadata || '{}');
      } catch (error) {
        const notification = {
          open: true,
          type: 'error',
          message: 'Additional Metadata JSON is invalid. Please check the format.'
        };
        Framework.getStoreRegistry().getStore('notificationState').set(notification);
        return;
      }

      // Build GPT-5 config if GPT-5 or GPT-5-mini is selected
      const isGPT5Model = formData.modelName === 'gpt-5' || formData.modelName === 'gpt-5-mini';
      const gpt5Config = isGPT5Model
        ? {
            reasoningEffort: formData.reasoningEffort || 'medium',
            verbosity: formData.verbosity || 'medium'
          }
        : undefined;

      const updatedData = {
        ...nodeData,
        prompt: formData.prompt,
        agentId: formData.agentId,
        customType: formData.customType,
        intentType: formData.intentType,
        config: {
          ...nodeData.config,
          model: {
            provider: formData.modelProvider,
            model: formData.modelName,
            temperature: parseFloat(formData.temperature),
            maxTokens: parseInt(formData.maxTokens),
            promptType: formData.promptType
          },
          systemPrompt: formData.systemPrompt,
          memory: {
            type: formData.memoryType,
            maxMessages: parseInt(formData.maxMessages),
            reflectionThreshold: 5
          },
          toolCalling: {
            enabled: nodeType === 'agent', // Only enable tool calling for agents
            toolChoice: formData.toolChoice
          },
          otherParameters,
          additionalMetadata,
          parsers: formData.parsers,
          // Add agentConfig with GPT-5 specific fields if GPT-5 model is selected
          ...(isGPT5Model && {
            agentConfig: {
              ...nodeData.config?.agentConfig,
              reasoningEffort: gpt5Config.reasoningEffort,
              verbosity: gpt5Config.verbosity
            }
          })
        }
      };
      onSave(updatedData);
    }
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div style={modalStyles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalStyles.header}>
          <h2>{nodeType === 'tool' ? 'Edit Tool' : nodeType === 'supervisor' ? 'Edit Supervisor' : 'Edit Agent'}</h2>
          <button style={modalStyles.closeButton} onClick={onClose}>
            ×
          </button>
        </div>

        <div style={modalStyles.tabs}>
          <div
            style={{
              ...modalStyles.tab,
              ...(activeTab === 'basic' ? modalStyles.activeTab : {})
            }}
            onClick={() => setActiveTab('basic')}
          >
            Basic
          </div>
          {(nodeType === 'agent' || nodeType === 'supervisor') && (
            <div
              style={{
                ...modalStyles.tab,
                ...(activeTab === 'model' ? modalStyles.activeTab : {})
              }}
              onClick={() => setActiveTab('model')}
            >
              Model
            </div>
          )}
          {(nodeType === 'agent' || nodeType === 'supervisor') && (
            <div
              style={{
                ...modalStyles.tab,
                ...(activeTab === 'memory' ? modalStyles.activeTab : {})
              }}
              onClick={() => setActiveTab('memory')}
            >
              Memory
            </div>
          )}
          {(nodeType === 'agent' || nodeType === 'supervisor') && (
            <div
              style={{
                ...modalStyles.tab,
                ...(activeTab === 'advanced' ? modalStyles.activeTab : {})
              }}
              onClick={() => setActiveTab('advanced')}
            >
              Advanced
            </div>
          )}
          {nodeType === 'tool' && (
            <div
              style={{
                ...modalStyles.tab,
                ...(activeTab === 'implementation' ? modalStyles.activeTab : {})
              }}
              onClick={() => setActiveTab('implementation')}
            >
              Implementation
            </div>
          )}
          {nodeType === 'tool' && (
            <div
              style={{
                ...modalStyles.tab,
                ...(activeTab === 'advanced' ? modalStyles.activeTab : {})
              }}
              onClick={() => setActiveTab('advanced')}
            >
              Advanced
            </div>
          )}
        </div>

        <form style={modalStyles.form} onSubmit={handleSubmit}>
          {/* Basic Tab Content */}
          {activeTab === 'basic' && (
            <div style={modalStyles.tabContent}>
              {(nodeType === 'agent' || nodeType === 'supervisor') && (
                <>
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>Display Name</label>
                    <input
                      type="text"
                      name="prompt"
                      value={formData.prompt || ''}
                      onChange={handleChange}
                      style={modalStyles.input}
                    />
                  </div>
                  {nodeType === 'agent' && (
                    <div style={modalStyles.formGroup}>
                      <label style={modalStyles.label}>Secondary Name</label>
                      <input
                        type="text"
                        name="intentType"
                        value={formData.intentType || ''}
                        onChange={handleChange}
                        style={modalStyles.input}
                      />
                    </div>
                  )}

                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>{nodeType === 'supervisor' ? 'Supervisor ID' : 'Agent ID'}</label>
                    <input
                      type="text"
                      name="agentId"
                      value={formData.agentId || ''}
                      onChange={handleChange}
                      style={modalStyles.input}
                      disabled
                    />
                  </div>
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>Prompt Types</label>
                    <select
                      name="promptType"
                      value={formData.promptType}
                      onChange={handleChange}
                      style={modalStyles.select}
                    >
                      <option value="">Select Prompt Types</option>
                      {promptTypesList.map((prompt) => (
                        <option key={prompt} value={prompt}>
                          {prompt}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>System Prompt</label>
                    <textarea
                      name="systemPrompt"
                      value={formData.systemPrompt || ''}
                      onChange={handleChange}
                      style={modalStyles.textarea}
                      placeholder="Instructions for the agent..."
                    />
                  </div>
                </>
              )}

              {nodeType === 'tool' && (
                <>
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>Tools List</label>
                    {tools.length > 0 ? (
                      <select style={modalStyles.input} value={selected} onChange={handleToolChange}>
                        <option value="" disabled>
                          Select a Tool
                        </option>
                        {tools?.map((tool) => (
                          <option key={tool.toolId} value={tool?.toolId}>
                            {`${tool?.label}`}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input type="text" value="Loading Tools..." style={modalStyles.input} disabled />
                    )}
                  </div>
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>Tool Name</label>
                    <input
                      type="text"
                      name="tool_name"
                      value={formData.tool_name || ''}
                      onChange={handleChange}
                      style={modalStyles.input}
                    />
                  </div>
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>Tool ID</label>
                    <input
                      type="text"
                      name="toolId"
                      value={formData.toolId || ''}
                      onChange={handleChange}
                      style={modalStyles.input}
                      disabled
                    />
                  </div>
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>Description</label>
                    <textarea
                      name="description"
                      value={formData.description || ''}
                      onChange={handleChange}
                      style={modalStyles.textarea}
                      placeholder="Describe what this tool does..."
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Model Tab Content */}
          {activeTab === 'model' && (nodeType === 'agent' || nodeType === 'supervisor') && (
            <div style={modalStyles.tabContent}>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Provider</label>
                <select
                  name="modelProvider"
                  value={formData.modelProvider}
                  onChange={handleChange}
                  style={modalStyles.select}
                >
                  <option value="">Select Provider</option>
                  {providerOptions.map((provider) => (
                    <option key={provider} value={provider}>
                      {provider}
                    </option>
                  ))}
                </select>
              </div>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Model Name</label>
                <select name="modelName" value={formData.modelName} onChange={handleChange} style={modalStyles.select}>
                  <option value="">Select Model</option>
                  {modelOptions.map((model) => (
                    <option key={model} value={model}>
                      {model}
                    </option>
                  ))}
                </select>
              </div>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Temperature</label>
                <input
                  type="number"
                  name="temperature"
                  value={formData.temperature || 0}
                  onChange={handleNumberChange}
                  min="0"
                  max="2"
                  step="0.1"
                  style={modalStyles.input}
                />
              </div>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Max Tokens</label>
                <input
                  type="number"
                  name="maxTokens"
                  value={formData.maxTokens || 0}
                  onChange={handleNumberChange}
                  min="1"
                  style={modalStyles.input}
                />
              </div>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Tool Choice</label>
                <select
                  name="toolChoice"
                  value={formData.toolChoice || 'auto'}
                  onChange={handleChange}
                  style={modalStyles.select}
                >
                  <option value="auto">Auto</option>
                  <option value="any">Any</option>
                  <option value="none">None</option>
                </select>
              </div>

              {/* GPT-5 specific fields - show only when GPT-5 or GPT-5-mini is selected */}
              {(formData.modelName === 'gpt-5' || formData.modelName === 'gpt-5-mini') && (
                <>
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>Reasoning Effort</label>
                    <select
                      name="reasoningEffort"
                      value={formData.reasoningEffort || 'medium'}
                      onChange={handleChange}
                      style={modalStyles.select}
                    >
                      <option value="minimal">Minimal</option>
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                  <div style={modalStyles.formGroup}>
                    <label style={modalStyles.label}>Verbosity</label>
                    <select
                      name="verbosity"
                      value={formData.verbosity || 'medium'}
                      onChange={handleChange}
                      style={modalStyles.select}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                    </select>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Memory Tab Content */}
          {activeTab === 'memory' && (nodeType === 'agent' || nodeType === 'supervisor') && (
            <div style={modalStyles.tabContent}>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Memory Type</label>
                <select
                  name="memoryType"
                  value={formData.memoryType || 'buffer'}
                  onChange={handleChange}
                  style={modalStyles.select}
                >
                  <option value="buffer">Buffer</option>
                  <option value="summary">Summary</option>
                  <option value="none">None</option>
                </select>
              </div>
              {formData.memoryType !== 'none' && (
                <div style={modalStyles.formGroup}>
                  <label style={modalStyles.label}>Max Messages</label>
                  <input
                    type="number"
                    name="maxMessages"
                    value={formData.maxMessages || 10}
                    onChange={handleNumberChange}
                    min="1"
                    style={modalStyles.input}
                  />
                </div>
              )}
            </div>
          )}

          {/* Advanced Tab Content for Agent */}
          {activeTab === 'advanced' && (nodeType === 'agent' || nodeType === 'supervisor') && (
            <>
              <div style={modalStyles.tabContent}>
                <div style={modalStyles.formGroup}>
                  <label style={modalStyles.label}>Other Parameters (JSON)</label>
                  <textarea
                    name="otherParameters"
                    value={formData.otherParameters || '{}'}
                    onChange={handleChange}
                    style={modalStyles.textarea}
                    placeholder='{"top_p": 0.9, "frequency_penalty": 0.5}'
                  />
                </div>
              </div>
              {nodeType === 'agent' && (
                <>
                  <div style={modalStyles.tabContent}>
                    <div style={modalStyles.formGroup}>
                      <label style={modalStyles.label}>{ADDITIONAL_METADATA_LABEL}</label>
                      <textarea
                        name="additionalMetadata"
                        value={formData.additionalMetadata}
                        onChange={handleChange}
                        style={modalStyles.textarea}
                        placeholder={ADDITIONAL_METADATA_PLACEHOLDER}
                      />
                    </div>
                  </div>
                  <div style={modalStyles.tabContent}>
                    <label style={modalStyles.label}>{LABEL_PARSERS}</label>
                    <FormGroup row>
                      {availableParsers?.length > 0 &&
                        availableParsers.map((parser, index) => (
                          <FormControlLabel
                            key={`${parser}-${index}`}
                            control={
                              <Checkbox
                                name={parser}
                                checked={(formData.parsers || []).includes(parser)}
                                style={modalStyles.checkbox}
                                onChange={handleCheckboxChange}
                              />
                            }
                            label={
                              <Typography variant="h6" style={modalStyles.checkbox}>
                                {parser}
                              </Typography>
                            }
                          />
                        ))}
                    </FormGroup>
                  </div>
                </>
              )}
            </>
          )}

          {/* Implementation Tab Content */}
          {activeTab === 'implementation' && nodeType === 'tool' && (
            <div style={modalStyles.tabContent}>
              {/* Parameters Section*/}
              {!isMCPServer && (
                <div style={modalStyles.formGroup}>
                  <label style={modalStyles.label}>Parameters</label>
                  <div style={{ marginBottom: '10px' }}>
                    <button
                      type="button"
                      onClick={addParameter}
                      style={{ ...modalStyles.button, backgroundColor: '#4ade80' }}
                    >
                      Add Parameter
                    </button>
                  </div>
                  {parameters.map((param, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '2fr 1fr 3fr 1fr auto',
                        gap: '8px',
                        marginBottom: '8px'
                      }}
                    >
                      <input
                        type="text"
                        value={param.name}
                        onChange={(e) => handleParameterChange(index, 'name', e.target.value)}
                        placeholder="Name"
                        style={modalStyles.input}
                      />
                      <select
                        value={param.type}
                        onChange={(e) => handleParameterChange(index, 'type', e.target.value)}
                        style={modalStyles.select}
                      >
                        <option value="number">number</option>
                        <option value="string">string</option>
                        <option value="boolean">boolean</option>
                        <option value="stringArray">array of string</option>
                        <option value="booleanArray">array of boolean</option>
                        <option value="numberArray">array of number</option>
                      </select>
                      <input
                        type="text"
                        value={param.description}
                        onChange={(e) => handleParameterChange(index, 'description', e.target.value)}
                        placeholder="Description"
                        style={modalStyles.input}
                      />
                      <input
                        type="checkbox"
                        checked={param.required}
                        onChange={(e) => handleParameterChange(index, 'required', e.target.checked)}
                        style={{ margin: 'auto' }}
                      />
                      <button
                        type="button"
                        onClick={() => removeParameter(index)}
                        style={{ ...modalStyles.button, backgroundColor: '#ef4444' }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {isCustomTool && (
                <>
                  {/* Check if this is an MCPServer tool */}
                  {(() => {
                    if (isMCPServer) {
                      // MCPServer - show only MCP Config JSON field
                      return (
                        <div style={modalStyles.formGroup}>
                          <label style={modalStyles.label}>MCP Config</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '10px' }}>
                            <button
                              type="button"
                              onClick={validateMcpConfig}
                              style={{ ...modalStyles.button, backgroundColor: '#4ade80' }}
                            >
                              Validate JSON
                            </button>
                          </div>
                          <textarea
                            value={mcpConfig}
                            onChange={handleMcpConfigChange}
                            style={{
                              ...modalStyles.textarea,
                              height: '200px',
                              borderColor:
                                mcpConfigValidation.showValidation && !mcpConfigValidation.isValid ? '#ef4444' : '#444',
                              fontFamily: 'monospace'
                            }}
                            placeholder='{"serverName": "example-server", "method": "POST", "authType": "None", "headers": {}, "payload": {}}'
                          />
                          {mcpConfigValidation.showValidation && !mcpConfigValidation.isValid && (
                            <div
                              style={{
                                ...modalStyles.validationMessage,
                                ...modalStyles.errorMessage
                              }}
                            >
                              ❌ Invalid JSON: {mcpConfigValidation.error}
                            </div>
                          )}
                          {mcpConfigValidation.showValidation && mcpConfigValidation.isValid && (
                            <div
                              style={{
                                ...modalStyles.validationMessage,
                                ...modalStyles.validMessage
                              }}
                            >
                              ✅ Valid JSON
                            </div>
                          )}
                        </div>
                      );
                    } else {
                      // RestAPI - show individual fields
                      return (
                        <>
                          <div style={modalStyles.formGroup}>
                            <label style={modalStyles.label}>URL</label>
                            <input
                              type="text"
                              name="endpoint"
                              value={formData.endpoint || ''}
                              onChange={handleChange}
                              style={modalStyles.input}
                              placeholder="https://api.example.com/path"
                            />
                          </div>
                          <div style={modalStyles.formGroup}>
                            <label style={modalStyles.label}>Authentication</label>
                            <select
                              name="authType"
                              value={formData.authType || 'None'}
                              onChange={handleChange}
                              style={modalStyles.select}
                            >
                              <option value="None">None</option>
                              <option value="IES">IES</option>
                              <option value="apiKey">API Key</option>
                            </select>
                          </div>
                          <div style={modalStyles.formGroup}>
                            <label style={modalStyles.label}>Method</label>
                            <select
                              name="method"
                              value={formData.method || 'None'}
                              onChange={handleChange}
                              style={modalStyles.select}
                            >
                              <option value="None">None</option>
                              <option value="GET">GET</option>
                              <option value="POST">POST</option>
                            </select>
                          </div>

                          {/* Headers Section - Always visible */}
                          <div style={modalStyles.formGroup}>
                            <label style={modalStyles.label}>Headers</label>
                            <div style={{ marginBottom: '10px' }}>
                              <button
                                type="button"
                                onClick={addHeader}
                                style={{ ...modalStyles.button, backgroundColor: '#4ade80' }}
                              >
                                Add Header
                              </button>
                            </div>
                            {headers.map((header, index) => (
                              <div
                                key={index}
                                style={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr 1fr auto',
                                  gap: '8px',
                                  marginBottom: '8px'
                                }}
                              >
                                <input
                                  type="text"
                                  value={header.key}
                                  onChange={(e) => handleHeaderChange(index, 'key', e.target.value)}
                                  placeholder="Header Key"
                                  style={modalStyles.input}
                                />
                                <input
                                  type="text"
                                  value={header.value}
                                  onChange={(e) => handleHeaderChange(index, 'value', e.target.value)}
                                  placeholder="Header Value"
                                  style={modalStyles.input}
                                />
                                <button
                                  type="button"
                                  onClick={() => removeHeader(index)}
                                  style={{ ...modalStyles.button, backgroundColor: '#ef4444' }}
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>

                          {/* Payload Section - Show only when POST method is selected */}
                          {formData.method === 'POST' && (
                            <div style={modalStyles.formGroup}>
                              <label style={modalStyles.label}>Payload</label>

                              {/* COMMENTED OUT - Field view no longer supported, JSON only */}
                              {/* {payloadMode === 'fields' ? (
                        <>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '10px' }}>
                            <button
                              type="button"
                              onClick={addPayloadField}
                              style={{ ...modalStyles.button, backgroundColor: '#4ade80' }}
                            >
                              Add Field
                            </button>
                            <div
                              onClick={togglePayloadMode}
                              style={{
                                display: 'flex',
                                backgroundColor: '#374151',
                                borderRadius: '20px',
                                padding: '2px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                border: '1px solid #4b5563'
                              }}
                            >
                              <div
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '18px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  transition: 'all 0.2s ease',
                                  backgroundColor: payloadMode === 'fields' ? '#4ade80' : 'transparent',
                                  color: payloadMode === 'fields' ? '#000' : '#e2e8f0'
                                }}
                              >
                                Fields
                              </div>
                              <div
                                style={{
                                  padding: '6px 12px',
                                  borderRadius: '18px',
                                  fontSize: '12px',
                                  fontWeight: '500',
                                  transition: 'all 0.2s ease',
                                  backgroundColor: payloadMode === 'json' ? '#4ade80' : 'transparent',
                                  color: payloadMode === 'json' ? '#000' : '#e2e8f0'
                                }}
                              >
                                JSON
                              </div>
                            </div>
                          </div>
                          {payloadFields.map((field, index) => (
                            <div
                              key={index}
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr auto',
                                gap: '8px',
                                marginBottom: '8px'
                              }}
                            >
                              <input
                                type="text"
                                value={field.key}
                                onChange={(e) => handlePayloadFieldChange(index, 'key', e.target.value)}
                                placeholder="Field Key"
                                style={modalStyles.input}
                              />
                              <input
                                type="text"
                                value={field.value}
                                onChange={(e) => handlePayloadFieldChange(index, 'value', e.target.value)}
                                placeholder="Field Value (use {{placeholder}} for dynamic values)"
                                style={modalStyles.input}
                              />
                              <button
                                type="button"
                                onClick={() => removePayloadField(index)}
                                style={{ ...modalStyles.button, backgroundColor: '#ef4444' }}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </>
                      ) : ( */}
                              {/* JSON mode only - removed toggle switch */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '10px' }}>
                                <button
                                  type="button"
                                  onClick={validatePayloadJson}
                                  style={{ ...modalStyles.button, backgroundColor: '#4ade80' }}
                                >
                                  Validate JSON
                                </button>
                                {/* COMMENTED OUT - Mode toggle switch no longer needed, JSON only */}
                                {/* <div
                          onClick={togglePayloadMode}
                          style={{
                            display: 'flex',
                            backgroundColor: '#374151',
                            borderRadius: '20px',
                            padding: '2px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            border: '1px solid #4b5563'
                          }}
                        >
                          <div
                            style={{
                              padding: '6px 12px',
                              borderRadius: '18px',
                              fontSize: '12px',
                              fontWeight: '500',
                              transition: 'all 0.2s ease',
                              backgroundColor: payloadMode === 'fields' ? '#4ade80' : 'transparent',
                              color: payloadMode === 'fields' ? '#000' : '#e2e8f0'
                            }}
                          >
                            Fields
                          </div>
                          <div
                            style={{
                              padding: '6px 12px',
                              borderRadius: '18px',
                              fontSize: '12px',
                              fontWeight: '500',
                              transition: 'all 0.2s ease',
                              backgroundColor: payloadMode === 'json' ? '#4ade80' : 'transparent',
                              color: payloadMode === 'json' ? '#000' : '#e2e8f0'
                            }}
                          >
                            JSON
                          </div>
                        </div> */}
                              </div>
                              <textarea
                                value={payloadJson}
                                onChange={handlePayloadJsonChange}
                                style={{
                                  ...modalStyles.textarea,
                                  height: '150px',
                                  borderColor:
                                    payloadJsonValidation.showValidation && !payloadJsonValidation.isValid
                                      ? '#ef4444'
                                      : '#444',
                                  fontFamily: 'monospace'
                                }}
                                placeholder='{"key": "value", "placeholder": "{{dynamicValue}}"}'
                              />
                              {payloadJsonValidation.showValidation && !payloadJsonValidation.isValid && (
                                <div
                                  style={{
                                    ...modalStyles.validationMessage,
                                    ...modalStyles.errorMessage
                                  }}
                                >
                                  ❌ Invalid JSON: {payloadJsonValidation.error}
                                </div>
                              )}

                              <Alert
                                severity="info"
                                icon={<InfoIcon />}
                                sx={{
                                  mt: 1.5,
                                  mb: 1.5,
                                  backgroundColor: '#1f2937',
                                  border: '1px solid #374151',
                                  borderLeft: '4px solid #3b82f6',
                                  borderRadius: '12px',
                                  '& .MuiAlert-icon': {
                                    color: '#3b82f6'
                                  },
                                  '& .MuiAlert-message': {
                                    width: '100%'
                                  }
                                }}
                              >
                                <Box>
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 600,
                                      color: '#e2e8f0',
                                      mb: 0.75
                                    }}
                                  >
                                    Dynamic Placeholders
                                  </Typography>
                                  <Box
                                    sx={{
                                      color: '#d1d5db',
                                      fontSize: '14px',
                                      lineHeight: 1.5,
                                      mb: 1
                                    }}
                                  >
                                    Use{' '}
                                    <Chip
                                      label="{{"
                                      size="small"
                                      sx={{
                                        height: '20px',
                                        fontSize: '11px',
                                        backgroundColor: '#374151',
                                        color: '#fbbf24',
                                        fontFamily: 'monospace'
                                      }}
                                    />{' '}
                                    and{' '}
                                    <Chip
                                      label="}}"
                                      size="small"
                                      sx={{
                                        height: '20px',
                                        fontSize: '11px',
                                        backgroundColor: '#374151',
                                        color: '#fbbf24',
                                        fontFamily: 'monospace'
                                      }}
                                    />{' '}
                                    to create placeholders for dynamic values
                                  </Box>
                                  <Box
                                    sx={{
                                      color: '#6b7280',
                                      fontSize: '12px',
                                      fontStyle: 'italic',
                                      display: 'block'
                                    }}
                                  >
                                    Examples:{' '}
                                    <Chip
                                      label="{{ userId }}"
                                      size="small"
                                      sx={{
                                        height: '18px',
                                        fontSize: '10px',
                                        backgroundColor: '#374151',
                                        color: '#34d399',
                                        fontFamily: 'monospace',
                                        mx: 0.5
                                      }}
                                    />
                                    <Chip
                                      label="{{ timestamp }}"
                                      size="small"
                                      sx={{
                                        height: '18px',
                                        fontSize: '10px',
                                        backgroundColor: '#374151',
                                        color: '#34d399',
                                        fontFamily: 'monospace',
                                        mx: 0.5
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Alert>
                            </div>
                          )}
                        </>
                      );
                    }
                  })()}
                </>
              )}

              {!isCustomTool && (
                <div style={modalStyles.formGroup}>
                  <label style={modalStyles.label}>Response Format</label>
                  <input
                    type="text"
                    name="responseFormat"
                    value={formData.responseFormat || ''}
                    onChange={handleChange}
                    style={modalStyles.input}
                  />
                </div>
              )}

              {!isCustomTool && (
                <div style={modalStyles.formGroup}>
                  <label style={modalStyles.label}>Implementation</label>
                  <div style={IMPLEMENTATION_BUTTONS}>
                    <button
                      type="button"
                      onClick={regenerateImplementation}
                      style={{ ...modalStyles.button, backgroundColor: '#4ade80' }}
                    >
                      Regenerate Code
                    </button>
                    <button
                      type="button"
                      onClick={() => validateImplementation(formData?.implementation)}
                      style={{ ...modalStyles.button, backgroundColor: '#4ade80' }}
                    >
                      Validate Code
                    </button>
                  </div>
                  <textarea
                    value={formData.implementation || ''}
                    onChange={handleImplementationChange}
                    style={{
                      ...modalStyles.textarea,
                      height: '200px',
                      borderColor: jsValidation.isValid ? '#444' : '#ef4444'
                    }}
                  />

                  {jsValidation.errors.length > 0 && (
                    <div>
                      {jsValidation.errors.map((error, index) => (
                        <div
                          key={`error-${index}`}
                          style={{
                            ...modalStyles.validationMessage,
                            ...modalStyles.errorMessage
                          }}
                        >
                          ❌ {error}
                        </div>
                      ))}
                    </div>
                  )}

                  {jsValidation.warnings.length > 0 && (
                    <div>
                      {jsValidation.warnings.map((warning, index) => (
                        <div
                          key={`warning-${index}`}
                          style={{
                            ...modalStyles.validationMessage,
                            ...modalStyles.warningMessage
                          }}
                        >
                          ⚠️ {warning}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Advanced Tab Content for Tool */}
          {activeTab === 'advanced' && nodeType === 'tool' && (
            <div style={modalStyles.tabContent}>
              <div style={modalStyles.formGroup}>
                <label style={modalStyles.label}>Advanced Options (JSON)</label>
                <textarea
                  name="advancedOptions"
                  value={formData.advancedOptions || '{}'}
                  onChange={handleChange}
                  style={modalStyles.textarea}
                  placeholder='{"timeout": 3000, "retry": 1}'
                />
              </div>
            </div>
          )}

          <div style={modalStyles.footer}>
            <button type="button" onClick={onClose} style={{ ...modalStyles.button, ...modalStyles.cancelButton }}>
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...modalStyles.button,
                ...modalStyles.saveButton,
                cursor: !canEditGraph ? 'not-allowed' : 'pointer',
                opacity: !canEditGraph ? 0.6 : 1
              }}
              disabled={!canEditGraph}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditNodeModal;
